import _ from 'lodash';
import psHttp, { psAxios } from '@patsnap/common-ps-http';
import { createUniformSetting } from '@patsnap/uniform-setting';
import { HeaderService, SLD, Site } from '@patsnap-utils/biz';
import { prepareTokenBase, updateTokenBaseRequestHeaders } from './enable-token-base';
import { handleLogout } from '@patsnap-utils/passport';
import { setWebsiteFont } from './configure-website-font';
// 静态导入常量（用于立即使用）
import { productShellDeferred, accountDeferred } from './defer-constants';
import { addTrialService } from '../add-trial/add-trial-service';
import type { IProjectPreset } from './enable-token-base';
import type { AxiosInstance } from 'axios';
import type { T_Locale, T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import {
    customProductsLinkInUserCenter,
    type CustomLinksOptions,
    type EurekaAmountsControlForAvatarMenu,
    type PackageForAvatarMenu,
} from './custom-links';
import { planDeferred } from '../account';

/**
 * I18n 实例接口（用于语言切换）
 */
export interface I18nInstance {
    /**
     * 当前语言
     * 兼容 vue-i18n Composition API 和 Legacy API
     */
    locale: {
        value: string;
    } | string;
    /**
     * 设置语言消息
     */
    setLocaleMessage?: (locale: string, messages: Record<string, any>) => void;
}

export interface IStartupOption<T = unknown> {
    /**
     * 是否禁用token拦截器
     * @description 在分享页面中，禁用token拦截器
     */
    disablePrepareToken?: boolean
    /**
     * 禁用自动赋权
     */
    disableTrial?: boolean
    headerService: HeaderService
    projectName: string
    preset: IProjectPreset
    pageInit: () => Promise<T>
    /**
     * i18n 实例（可选）
     * @description 如果提供，切换语言时将调用 i18n 实例，而不是刷新页面
     */
    i18n?: I18nInstance
    /**
     * i18n、字体和请求头完成切换后触发
     */
    onLocaleChanged?: (locale: T_Locale) => void | Promise<void>
}

interface StartupHandlerOptions<T> {
    projectName: string;
    headerService: HeaderService
    locale: T_Locale
    setting: ReturnType<typeof createUniformSetting>
    pageData: T
    i18n?: I18nInstance
    onLocaleChanged?: (locale: T_Locale) => void | Promise<void>
}

type StartupResponse<T> = [
    HeaderService,
    T_Locale,
    T,
    ReturnType<typeof createUniformSetting>
];

async function fetchAmountsControlForAvatarMenu(isLoggedIn: boolean): Promise<EurekaAmountsControlForAvatarMenu | null> {
    if (!isLoggedIn) {
        return null;
    }

    try {
        const res = await psHttp({
            method: 'GET',
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/amounts/control`,
        });
        return _.get(res, 'data.data', null) || _.get(res, 'data', null);
    } catch (error) {
        console.warn('[startup] Failed to get amounts control for avatar menu:', error);
        return null;
    }
}

async function fetchPackagesForAvatarMenu(isLoggedIn: boolean): Promise<PackageForAvatarMenu[] | null> {
    if (!isLoggedIn) {
        return null;
    }

    try {
        const res = await psHttp({
            method: 'GET',
            url: `${import.meta.env.VITE_EUREKA_SERVICE_URI}/eureka/package-info`,
            transformCode: true,
            headers: {
                'x-api-version': '1.0',
            },
        });
        const packages = _.get(res, 'data.data', null) || _.get(res, 'data', null);
        return Array.isArray(packages) ? packages : [];
    } catch (error) {
        console.warn('[startup] Failed to get package info for avatar menu:', error);
        return null;
    }
}

window.productShellReady = productShellDeferred;

const supportLocales: T_Locale[] = ['cn', 'en', 'jp', 'tw'];

const homePageHref = new Site({ sld: SLD.ANALYTICS }).href;

async function getPrepareTokenBase(preset?: IProjectPreset) {
    const passport = await prepareTokenBase(preset);
    return passport;
}

async function initializeHeaderService(headerService: HeaderService) {
    headerService.logoHref = homePageHref;

    try {
        await headerService.sync({
            http: psHttp,
            uri: import.meta.env.VITE_ACCOUNT_SERVICE_URI as string,
        });

        if (headerService.account) {
            accountDeferred.resolve(headerService.account);
        }
    } catch (error) {
        headerService.syncing = true;
        throw error;
    } finally {
        // headerService.visibleProducts = false;
    }
}

async function initializeSetting() {
    const setting = createUniformSetting({
        verbose: true,
        axiosInstance: psAxios as AxiosInstance as any,
        loggedIn: true,
        baseAccountUrl: import.meta.env.VITE_ACCOUNT_SERVICE_URI,
        locale: {
            supports: supportLocales,
        },
        region: import.meta.env.VITE_DEPLOY_ENV as T_Region,
    });

    const settingData = await setting.locale.getLocale();
    const locale = settingData.user || settingData.default;

    setWebsiteFont(locale);
    updateTokenBaseRequestHeaders({ 'X-Site-Lang': locale.toUpperCase() });

    return { locale, setting };
}

function startupHandler<T>(options: StartupHandlerOptions<T>): StartupResponse<T> {
    const { projectName, headerService, locale, setting, pageData, i18n, onLocaleChanged } = options;
    const isFree = _.get(pageData, 'plan.plan_type') === 'Free';
    const accountInfo = headerService?.account as any;
    const companyId = accountInfo?._info?.company_id || accountInfo?._info_?.company_id;
    const isPersonal = !companyId;
    const isLoggedIn = !!headerService?.userCenterService?.logged;
    planDeferred.resolve({
        isFreemium: isFree && isLoggedIn,
        isPersonal: isPersonal && isLoggedIn,
    });

    headerService.visibleProducts = !isPersonal;
    const avatarMenuOptions: CustomLinksOptions = {
        isEnterprise: !isPersonal && isLoggedIn,
        isPersonal: isPersonal && isLoggedIn,
        locale,
        packages: [],
        packagesLoaded: false,
        control: null,
    };
    fetchAmountsControlForAvatarMenu(isLoggedIn).then((control) => {
        avatarMenuOptions.control = control;
    });
    fetchPackagesForAvatarMenu(isLoggedIn).then((packages) => {
        avatarMenuOptions.packages = packages || [];
        avatarMenuOptions.packagesLoaded = packages !== null;
    });

    customProductsLinkInUserCenter(headerService, avatarMenuOptions);

    /* WhiteLabel 时，隐藏 PatSnap Logo */
    if (headerService.whiteLabel) {
        headerService.logoVisible = false;
    }

    /* 注册网站语言 */
    const { locales } = headerService;
    type HeaderLocaleItem = typeof locales[number];
    headerService.locales = _.filter(locales, (localeItem: HeaderLocaleItem) => _.includes(supportLocales, localeItem.key)).map(({ ...rest }) => {
        return {
            ...rest,
            active: rest.key === locale,
        };
    });

    /* 注册切换语言函数 */
    const beforeLocaleChangeHandler: HeaderService['beforeLocaleChange'] = async (localeKey, done) => {
        await setting.locale.setLocale(localeKey as T_Locale);

        // 如果提供了 i18n 实例，使用 i18n 切换语言，否则刷新页面
        if (i18n) {
            // 切换 i18n 语言（兼容 Composition API 和 Legacy API）
            if (typeof i18n.locale === 'object' && 'value' in i18n.locale) {
                // Composition API 模式
                i18n.locale.value = localeKey;
            } else if (typeof i18n.locale === 'string') {
                // Legacy API 模式
                (i18n as any).locale = localeKey;
            }

            // 更新字体
            setWebsiteFont(localeKey as T_Locale);

            // 更新 HTTP 请求头
            updateTokenBaseRequestHeaders({ 'X-Site-Lang': localeKey.toUpperCase() });

            try {
                await onLocaleChanged?.(localeKey as T_Locale);
            } catch (error) {
                console.warn('[startup] Failed to handle locale change:', error);
            }

            // 完成回调
            done();

            document.body.click();
        } else {
            // 降级方案：刷新页面
            window.location.reload();
            done();
        }
    };
    headerService.beforeLocaleChange = beforeLocaleChangeHandler;

    /* 注册登出回调函数 */
    const logoutHandler: HeaderService['userCenterService']['logoutHandler'] = async (_done, logoutHref) => {
        const data: unknown = await handleLogout();
        setTimeout(() => {
            const href = _.get(data, 'data.redirect_url', logoutHref);
            window.location.href = href;
        }, 1000);
    };
    headerService.userCenterService.logoutHandler = logoutHandler;

    /* header头切换租户成功后页面跳转 */
    headerService.switchTenantHandler = async (_tenantId, done) => {
        return done().then(() => {
            window.location.href = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href;
        });
    };

    /* 九宫格的微前端在header信息拿到之前就立马加载的话，会劫持defer.js和favorites-browser.js，导致部分页面七鱼样式加载不出来 */
    if (headerService.account) {
        productShellDeferred.resolve(async () => {
            const { defer } = await import(/* webpackChunkName: "defer" */'./defer');
            defer({ projectName }, headerService.account!, locale);
        });
    }

    return [headerService, locale, pageData, setting];
}

async function startup<PageData>(startupOption: IStartupOption<PageData>) {
    let passport: Awaited<ReturnType<typeof getPrepareTokenBase>> | undefined;
    if (!startupOption.disablePrepareToken) {
        passport = await getPrepareTokenBase(startupOption.preset);
    }

    const headerService = startupOption.headerService;
    const projectName = startupOption.projectName;
    const i18n = startupOption.i18n;
    const onLocaleChanged = startupOption.onLocaleChanged;

    try {
        const data = await Promise.all([
            startupOption.pageInit().catch(e => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) return Promise.reject(e);
            }),
            initializeSetting(),
            initializeHeaderService(headerService).then(() => {
                if (startupOption.disableTrial) return;
                return addTrialService.addTrial(headerService, passport ?? undefined);
            }),
        ]);
        const [pageData, { locale, setting }] = data;
        return startupHandler({
            projectName,
            headerService,
            locale,
            setting,
            pageData: pageData as PageData,
            i18n,
            onLocaleChanged,
        });
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export {
    startup,
};
